import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../common/Modal';
import { Select } from '../../ui/Select/Select';
import { fetchUserByEmail } from '../../../api/users.api';
import { FREQUENT_EMULATED_USERS } from '../../../constants/reportBugConstants';
import { beginEmulation } from '../../../utils/emulation';
import { logger } from '../../../utils/logger';

interface EmulationModalProps {
  open: boolean;
  onClose: () => void;
}

type SourceMode = 'email' | 'frequent';

const HTTP_OK = 200;

// ── Tailwind class bundles ────────────────────────────────────────────────
const OPTION_ROW_CLASS = 'flex items-start gap-[10px] py-2';

const RADIO_CLASS =
  'flex-none mt-[10px] cursor-pointer [accent-color:var(--color-accent)]';

const FIELD_COL_CLASS = 'flex-1 flex flex-col gap-1 min-w-0';

// Shared text-input / select base — matches the `.emulation-modal__input`
// and `.emulation-modal__select` rules. Focus state lifts the border to the
// accent color and adds a soft ring.
const INPUT_BASE =
  'w-full h-9 py-[6px] px-[10px] rounded-md outline-none' +
  ' border border-solid border-[var(--color-border)]' +
  ' bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] text-[13px]' +
  ' transition-[border-color,box-shadow] duration-[150ms] ease-linear' +
  ' placeholder:text-[var(--color-text-muted)]' +
  ' focus:border-[var(--color-accent)] focus:shadow-[0_0_0_2px_var(--color-accent-soft)]';

// Error variant — danger-colored border + ring. Also keeps the ring on focus.
const INPUT_ERROR_EXTRA =
  ' !border-[var(--color-danger)] focus:shadow-[0_0_0_2px_rgba(220,38,38,0.18)]';

const ERROR_TEXT_CLASS = 'text-[var(--color-danger)] text-xs';

const DIVIDER_ROW_CLASS = 'flex items-center gap-2 mt-3 mb-2';
const DIVIDER_LINE_CLASS = 'flex-1 h-px bg-[var(--color-border)]';
const DIVIDER_TEXT_CLASS =
  'text-xs text-[var(--color-text-muted)] tracking-[0.05em] font-hk-semibold';

const HEADING_CLASS =
  'mt-0 mb-1 mx-0 text-[13px] font-hk-medium text-[var(--color-text-secondary)]';

// Footer buttons — secondary (outlined) and primary (green pill).
const BTN_BASE =
  'h-[34px] px-4 rounded-md text-[13px] font-hk-medium cursor-pointer' +
  ' border border-solid border-transparent' +
  ' transition-[background-color,color,border-color] duration-[150ms] ease-linear' +
  ' disabled:opacity-50 disabled:cursor-not-allowed';
const BTN_SECONDARY =
  ' bg-transparent border-[var(--color-border-strong)] text-[var(--color-text-primary)]' +
  ' enabled:hover:bg-[var(--color-bg-hover)]';
const BTN_PRIMARY =
  ' bg-[#16a34a] text-white border-[#16a34a]' +
  ' enabled:hover:bg-[#15803d] enabled:hover:border-[#15803d]';

const EmulationModal: React.FC<EmulationModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<SourceMode>('email');
  const [emailInput, setEmailInput] = useState<string>('');
  const [selectedFrequent, setSelectedFrequent] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  /**
   * Mirrors Vue's `isEmailValid` — flipped true ONLY after the server
   * returns a non-200 from the user lookup. Pre-flight regex is intentionally
   * skipped to match the Vue UX.
   */
  const [showInvalid, setShowInvalid] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      setMode('email');
      setEmailInput('');
      setSelectedFrequent('');
      setSubmitting(false);
      setShowInvalid(false);
    }
  }, [open]);

  const candidateEmail =
    mode === 'email' ? emailInput.trim() : selectedFrequent;
  const confirmDisabled = submitting || candidateEmail.length === 0;
  const emailInvalid = showInvalid && mode === 'email';

  const handleSelectMode = useCallback((next: SourceMode): void => {
    setMode(next);
    setShowInvalid(false);
    if (next === 'email') {
      setSelectedFrequent('');
    } else {
      setEmailInput('');
    }
  }, []);

  const handleConfirm = useCallback(async (): Promise<void> => {
    if (confirmDisabled) return;
    setSubmitting(true);
    setShowInvalid(false);
    try {
      const response = await fetchUserByEmail(candidateEmail);
      if (response.status === HTTP_OK && 'data' in response && response.data) {
        // Awaits the cross-origin mirror to Vue before reloading so the sync
        // iframe completes before navigation tears it down.
        await beginEmulation(response.data);
        // Mirrors Vue's pageReload() — every store/cache rebuilds against
        // the emulated identity on the next mount.
        window.location.reload();
        return;
      }
      setShowInvalid(true);
      setSelectedFrequent('');
    } catch (err) {
      logger.error('Emulation: fetchUserByEmail threw', err);
      setShowInvalid(true);
      setSelectedFrequent('');
    } finally {
      setSubmitting(false);
    }
  }, [candidateEmail, confirmDisabled]);

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title={t('emulation.modalTitle')}
      size="md"
      footer={
        <>
          <button
            type="button"
            className={BTN_BASE + BTN_SECONDARY}
            onClick={onClose}
            disabled={submitting}
          >
            {t('emulation.cancel')}
          </button>
          <button
            type="button"
            className={BTN_BASE + BTN_PRIMARY}
            onClick={handleConfirm}
            disabled={confirmDisabled}
          >
            {submitting ? t('emulation.submitting') : t('emulation.confirm')}
          </button>
        </>
      }
    >
      <div className={OPTION_ROW_CLASS}>
        <input
          type="radio"
          name="emulation-mode"
          className={RADIO_CLASS}
          checked={mode === 'email'}
          onChange={() => handleSelectMode('email')}
          aria-label={t('emulation.byEmail')}
          disabled={submitting}
        />
        <div className={FIELD_COL_CLASS}>
          <input
            type="email"
            className={INPUT_BASE + (emailInvalid ? INPUT_ERROR_EXTRA : '')}
            placeholder={t('emulation.emailPlaceholder')}
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              setShowInvalid(false);
            }}
            onFocus={() => handleSelectMode('email')}
            disabled={submitting}
          />
          {emailInvalid ? (
            <small className={ERROR_TEXT_CLASS}>
              {t('emulation.invalidEmail')}
            </small>
          ) : null}
        </div>
      </div>

      <div className={DIVIDER_ROW_CLASS}>
        <span className={DIVIDER_LINE_CLASS} aria-hidden />
        <span className={DIVIDER_TEXT_CLASS}>
          {t('emulation.or')}
        </span>
        <span className={DIVIDER_LINE_CLASS} aria-hidden />
      </div>

      <p className={HEADING_CLASS}>{t('emulation.frequentHeading')}</p>

      <div className={OPTION_ROW_CLASS}>
        <input
          type="radio"
          name="emulation-mode"
          className={RADIO_CLASS}
          checked={mode === 'frequent'}
          onChange={() => handleSelectMode('frequent')}
          aria-label={t('emulation.frequentUsers')}
          disabled={submitting}
        />
        {/* Shared Select (the same primitive CompletenessModal mounts inside
            a Modal) — it portals its own listbox with the proper ARIA
            wiring, so this file no longer hand-rolls a `role="listbox"`
            on a <ul>. Picking a user is what switches the radio to the
            frequent-users mode. */}
        <div className="flex-1 min-w-0">
          <Select
            options={FREQUENT_EMULATED_USERS}
            value={selectedFrequent}
            onChange={(value) => {
              setSelectedFrequent(value);
              handleSelectMode('frequent');
            }}
            placeholder={t('emulation.selectUser')}
            ariaLabel={t('emulation.selectUser')}
            active={mode === 'frequent' && selectedFrequent.length > 0}
            disabled={submitting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default EmulationModal;
